import { PageLayout } from '@/layouts/page-layout'
import { useTranslation } from 'react-i18next'
import { useState, useRef, useEffect } from 'react'
import {
  Table,
  Header,
  HeaderRow,
  HeaderCell,
  Body,
  Row,
  Cell
} from '@table-library/react-table-library/table'
import { useTheme } from '@table-library/react-table-library/theme'
import { Button, Tooltip } from '@material-tailwind/react'
import {
  monthOptions,
  yearOptions
} from '@/components/attachment-uploader/utils'
import { FiTrash2, FiUpload, FiMenu } from 'react-icons/fi'
import CustomSelect from '@/shared/components/custom-select'
import { fileToAttachment } from '@/utils/file-to-attachment'
import { importApi, attachmentTypeApi } from '@/store/services'
import { store } from '@/store'
import { NotificationModal } from '@/components/notification-modal'
import { downloadMassiveImportReport } from '@/utils/massive-import-report'
import { ContractSelector } from '@/shared/selectors'

export const EmployeeMassiveImport = () => {
  const { t } = useTranslation()
  const theme = useTheme({
    Table: `
      --data-table-library_grid-template-columns: 2.5fr 2.5fr 1.5fr 1.5fr 1fr 1fr;
      table-layout: fixed;
    `,
    Cell: `
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 100px !important;
    `
  })

  const initialValue = {
    id: Date.now(),
    attachment_type: {},
    contractor_contract: {},
    period_month: '',
    period_year: '',
    files: []
  }

  const [rows, setRows] = useState([initialValue])
  const [attachmentTypes, setAttachmentTypes] = useState([])
  const inputRefs = useRef([])
  const [showNotificationModal, setShowNotificationModal] = useState(false)
  const [iconTypeNotificacionModal, setIconTypeNotificacionModal] = useState('')
  const [titleNotificacionModal, setTitleNotificationModal] = useState('')
  const [messageNotificacionModal, setMessageNotificationModal] = useState('')

  const addRow = () => {
    setRows((prevRows) => [...prevRows, { ...initialValue, id: Date.now() }])
  }

  const removeRow = (id) => {
    setRows((prevRows) => prevRows.filter((row) => row.id !== id))
  }

  useEffect(() => {
    const fetchAttachmentTypes = async () => {
      try {
        const { attachmentTypes } = await store
          .dispatch(
            attachmentTypeApi.endpoints.getAttachmentTypes.initiate(
              '?page=1&per_page=9999&klass=Employee&exclude_termination=true'
            )
          )
          .unwrap()

        const filteredAttachmentTypes = attachmentTypes.filter((option) => {
          const klass = option?.klass || option?.resource?.klass
          return klass === 'Employee'
        })

        setAttachmentTypes(
          filteredAttachmentTypes.map((option) => ({
            value: option.id,
            label: option.name,
            id: option.id,
            name: option.name,
            recurrence: option.recurrence
          }))
        )
      } catch (error) {
        console.error('Error fetching attachment types:', error)
      }
    }

    fetchAttachmentTypes()
  }, [])

  const handleInputChange = (id, field, value) => {
    setRows((prevRows) =>
      prevRows.map((row) =>
        row.id === id
          ? { ...row, [field]: value }
          : row
      )
    )
  }

  const handleFileChange = (event, id) => {
    const files = Array.from(event.target.files)
    Promise.all(
      files.map((file) =>
        new Promise((resolve) => {
          fileToAttachment(file, resolve)
        })
      )
    ).then((attachments) => {
      setRows((prevRows) =>
        prevRows.map((row) =>
          row.id === id ? { ...row, files: attachments } : row
        )
      )
    })
  }

  const handleClick = (index) => {
    inputRefs.current[index].click()
  }

  const validateRows = () => {
    for (const row of rows) {
      if (!row.attachment_type.value) {
        alert('Por favor selecciona un Tipo de documento en todas las filas.')
        return false
      }

      if (
        ['monthly'].includes(row.attachment_type.recurrence) &&
        !row.period_month
      ) {
        alert('Por favor selecciona un mes en todas las filas requeridas.')
        return false
      }

      if (
        ['monthly', 'yearly'].includes(row.attachment_type.recurrence) &&
        !row.period_year
      ) {
        alert('Por favor selecciona un año en todas las filas requeridas.')
        return false
      }

      if (row.files.length === 0) {
        alert('Por favor sube al menos un archivo en cada fila.')
        return false
      }
    }
    return true
  }

  const uploadRows = async () => {
    if (!validateRows()) {
      return
    }
    try {
      const { type, body } = { type: 'employees', body: { data: rows } }
      const response = await store.dispatch(
        importApi.endpoints.sendAttachmentFile.initiate({ type, body })
      ).unwrap()

      if (response.status_upload === 200) {
        setRows([initialValue])
        setIconTypeNotificacionModal('check')
        setTitleNotificationModal('Importación realizada exitosamente.')
        setMessageNotificationModal('Se importaron correctamente todos los archivos')
      } else {
        setIconTypeNotificacionModal('error')
        setTitleNotificationModal('Importación realizada con errores')
        setMessageNotificationModal(
          'Verifica el archivo de descarga con el reporte de la importación.'
        )
        downloadMassiveImportReport(response.errors)
      }
      setShowNotificationModal(true)
    } catch (error) {
      if (error && error.originalStatus === 413) {
        setIconTypeNotificacionModal('error')
        setTitleNotificationModal('No se pudo realizar la importación')
        setMessageNotificationModal(
          'Se superó el tamaño máximo de carga.'
        )
      } else {
        setIconTypeNotificacionModal('error')
        setTitleNotificationModal('No se pudo realizar la importación')
        setMessageNotificationModal(
          'Ha ocurrido un error con el servidor. Vuelve a intentarlo.'
        )
      }
      setShowNotificationModal(true)
    }
  }

  return (
    <PageLayout title={t('employee.attachmentImport')}>
      {showNotificationModal && (
        <NotificationModal
          title={titleNotificacionModal}
          message={messageNotificacionModal}
          iconType={iconTypeNotificacionModal}
          setBackStateFunction={setShowNotificationModal}
        ></NotificationModal>
      )}
      <Table
        data={{ nodes: rows }}
        layout={{ custom: true, fixedHeader: true }}
        theme={theme}
        className='h-max'
      >
        {(tableList) => (
          <>
            <Header>
              <HeaderRow>
                <HeaderCell>ID Contrato</HeaderCell>
                <HeaderCell>Tipo</HeaderCell>
                <HeaderCell>Mes</HeaderCell>
                <HeaderCell>Año</HeaderCell>
                <HeaderCell>Archivos</HeaderCell>
                <HeaderCell>Acciones</HeaderCell>
              </HeaderRow>
            </Header>

            <Body>
              {tableList.map((column, index) => (
                <Row key={column.id} item={column}>
                  <div>
                    <Cell>
                    <ContractSelector
                      value={column.contractor_contract.value}
                      onChange={(selectedOption) =>
                        handleInputChange(
                          column.id,
                          'contractor_contract',
                          selectedOption || ''
                        )
                      }
                      label=' '
                    />
                    </Cell>
                  </div>
                  <div>
                    <Cell>
                      <CustomSelect
                        options={attachmentTypes}
                        value={attachmentTypes.find(
                          (option) =>
                            option.value === column.attachment_type.value
                        )}
                        onChange={(selectedOption) =>
                          handleInputChange(
                            column.id,
                            'attachment_type',
                            selectedOption || ''
                          )
                        }
                        menuPortalTarget={document.body}
                      />
                    </Cell>
                  </div>
                  <div>
                    <Cell>
                      {['monthly'].includes(
                        attachmentTypes.find(
                          (option) =>
                            option.value === column.attachment_type.value
                        )?.recurrence
                      )
                        ? (
                        <CustomSelect
                          options={monthOptions}
                          menuPortalTarget={document.body}
                          value={monthOptions.find(
                            (option) =>
                              option.value === column.period_month
                          )}
                          onChange={(selectedOption) =>
                            handleInputChange(
                              column.id,
                              'period_month',
                              selectedOption ? selectedOption.value : ''
                            )
                          }
                        />
                          )
                        : (
                        <>-</>
                          )}
                    </Cell>
                  </div>
                  <div>
                    <Cell>
                      {['monthly', 'yearly'].includes(
                        attachmentTypes.find(
                          (option) =>
                            option.value === column.attachment_type.value
                        )?.recurrence
                      )
                        ? (
                        <CustomSelect
                          options={yearOptions()}
                          menuPortalTarget={document.body}
                          value={yearOptions().find(
                            (option) =>
                              option.value === column.period_year
                          )}
                          onChange={(selectedOption) =>
                            handleInputChange(
                              column.id,
                              'period_year',
                              selectedOption ? selectedOption.value : ''
                            )
                          }
                        />
                          )
                        : (
                        <>-</>
                          )}
                    </Cell>
                  </div>
                  <Cell>
                    <div className='flex items-center space-x-2'>
                      <input
                        type='file'
                        ref={(el) => (inputRefs.current[index] = el)}
                        multiple
                        style={{ display: 'none' }}
                        onChange={(event) => handleFileChange(event, column.id)}
                      />
                      <button
                        onClick={() => handleClick(index)}
                        className='flex items-center justify-center p-2 bg-transparent rounded hover:bg-gray-200 transition duration-300'
                      >
                        <FiUpload size={24} />
                      </button>
                      {column.files.length > 0 && (
                        <Tooltip
                          content={
                            <ul>
                              {column.files.map((file, idx) => (
                                <li key={idx}>{file.filename}</li>
                              ))}
                            </ul>
                          }
                          placement='left'
                        >
                          <span className="cursor-pointer hover:text-gray-200 transition-colors duration-300">
                            <FiMenu size={24} />
                          </span>
                        </Tooltip>
                      )}
                    </div>
                  </Cell>
                  <Cell>
                    <div className='flex items-center space'>
                      <Button onClick={() => removeRow(column.id)} color='red'>
                        <FiTrash2 />
                      </Button>
                    </div>
                  </Cell>
                </Row>
              ))}
            </Body>
          </>
        )}
      </Table>
      <div className='flex justify-between items-center'>
        <Button variant='outlined' color='white' onClick={addRow}>Agregar Fila</Button>
        <Button onClick={() => uploadRows()}>Cargar</Button>
      </div>
    </PageLayout>
  )
}
